import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import type { PaginatedResponse } from "../../types/pagination";
import { route } from "../../utils";
import {
  AddFileRecordToWorkspacePayload,
  AddFileRecordToWorkspaceResponse,
  BulkPresignFileRecordURLsItem,
  BulkPresignFileRecordURLsResponse,
  CopyFileRecordPayload,
  CreateFileRecordPayload,
  CreateFileRecordReferencePayload,
  CreateFileRecordReferenceResponse,
  FileRecord,
  FileRecordListResponse,
  FileRecordReferencesResponse,
  FileRecordResponse,
  FileRecordVersion,
  ListFileRecordVersionsResponse,
  ListFileRecordsParams,
  MoveFileRecordPayload,
  PresignUploadResponse,
  RecoverFileRecordResponse,
  RestoreFileRecordVersionResponse,
} from "./types";

export * from "./resourceUrls";
export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/file-records",
  SHARED_WITH_ME:
    "/api/v2/org/:organization_id/user/:user_id/shared-with-me/file-records",
  GET: "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id",
  CREATE: "/api/v2/org/:organization_id/user/:user_id/file-records",
  DELETE:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id",
  PRESIGN_UPLOAD:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/presign-upload",
  TIPTAP_TOKEN:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/tiptap-token",
  TIPTAP_ENABLE:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/tiptap-enable",
  TIPTAP_CONVERSION_TOKEN:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/tiptap-conversion-token",
  COPY: "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/copy",
  MOVE: "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/move",
  LIST_VERSIONS:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/versions",
  RESTORE_VERSION:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/versions/:file_record_version_id/restore",
  RECOVER:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/recover",
  PATCH_REGION:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/regions",
  PUBLISH:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/publish",
  UNPUBLISH:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/unpublish",
  BULK_PRESIGN_URLS:
    "/api/v2/org/:organization_id/user/:user_id/bulk-presign-file-record-urls",
  ARCHIVED: "/api/v2/org/:organization_id/user/:user_id/archived/file-records",
  RECENT: "/api/v2/org/:organization_id/user/:user_id/recent/file-records",
  REFERENCES:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/references",
  REFERENCES_LIST:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/references",
  DOWNLOAD:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/download",
  ADD_TO_LIBRARY:
    "/api/v2/org/:organization_id/user/:user_id/file-records/:file_record_id/add-to-workspace",
};

export type ArchivedFileRecordListResponse = PaginatedResponse<{
  file_records: FileRecord[];
}>;

export interface RecentFileRecordListResponse {
  file_records: FileRecord[];
  pagination: { has_more: boolean; next_ts?: string };
}

export interface PatchFileRecordRegionItem {
  region_id: string;
  content: string;
}

export interface PatchFileRecordRegionPayload {
  regions: PatchFileRecordRegionItem[];
}

export interface PatchFileRecordRegionResponse {
  file_record_id: string;
}

export interface TiptapTokenResponse {
  token: string;
  can_write: boolean;
  /** ISO/RFC3339 expiry for the JWT. */
  expires_at: string;
  /**
   * TipTap Collaboration document id (same format as the former `FileRecord.tiptap_document_name`).
   */
  document_name: string;
  /**
   * When true, S3 ↔ TipTap sync workflows are allowed for this file record.
   * Call `tiptapEnable` after first successful collab load (and markdown seed if needed).
   */
  tiptap_enabled: boolean;
}

export interface TiptapEnableResponse {
  file_record: FileRecord;
}

/** Server-enforced cap; mirrors `orgUserBulkPresignFileRecordURLsLimit`. */
export const BULK_PRESIGN_FILE_RECORD_URLS_LIMIT = 100;

/**
 * Result of `publishMany` — one entry per requested id, in input order.
 * Mirrors `Promise.allSettled` semantics so callers can surface partial
 * failures without losing track of which id failed.
 */
export type PublishManyResult =
  | { file_record_id: string; status: "fulfilled"; file_record: FileRecord }
  | { file_record_id: string; status: "rejected"; reason: unknown };

/**
 * Result of `unpublishMany` — symmetrical with `PublishManyResult`. Same
 * `Promise.allSettled` semantics; callers can surface partial failures
 * without losing track of which id failed.
 */
export type UnpublishManyResult = PublishManyResult;

export interface PresignFileRecordURLResponse {
  url: string;
  /** `true` when the URL forces a save (`Content-Disposition: attachment`). */
  download: boolean;
}

export interface TiptapConversionTokenResponse {
  token: string;
  /** X-App-Id for TipTap Conversion API (PDF export). */
  app_id: string;
  expires_at: string;
}

export class FileRecordsAPI {
  constructor(private client: CohesiveAPIClient) {}

  async list(
    params: ListFileRecordsParams,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const query: Record<string, string> = { class: params.class };
    if (params.workspace_id) query.workspace_id = params.workspace_id;
    if (params.canvas_id) query.canvas_id = params.canvas_id;
    if (params.flow_session_id) query.flow_session_id = params.flow_session_id;
    if (params.recently_deleted) query.recently_deleted = "true";
    if (params.kinds?.length) query.kinds = params.kinds.join(",");

    const response = await this.client.axios.get<{
      data: FileRecordListResponse;
    }>(
      route(
        ENDPOINTS.LIST,
        { organization_id: organizationId, user_id: userId },
        query,
      ),
      config,
    );
    return response.data.data.file_records;
  }

  /**
   * List file records shared directly with the current user (SpiceDB
   * viewer/editor grants on the file itself), independent of whether the user
   * can reach the file's parent canvas/workspace/flow-session.
   */
  async listSharedWithMe(config?: AxiosRequestConfig): Promise<FileRecord[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: FileRecordListResponse;
    }>(
      route(ENDPOINTS.SHARED_WITH_ME, {
        organization_id: organizationId,
        user_id: userId,
      }),
      config,
    );
    return response.data.data.file_records;
  }

  /**
   * List files the user created (folders excluded), most recently modified
   * first. Restricted server-side to workspaces and canvases the user can
   * currently view plus their own chats; files in archived scopes are omitted.
   * Supports `before_ts` keyset pagination (RFC3339Nano, from
   * `pagination.next_ts`).
   *
   * Unlike `listArchived`, this endpoint returns `pagination` only on the
   * top-level envelope, not inside `data`.
   */
  async listRecent(
    params?: { limit?: number; before_ts?: string },
    config?: AxiosRequestConfig,
  ): Promise<RecentFileRecordListResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: { file_records: FileRecord[] };
      pagination?: RecentFileRecordListResponse["pagination"];
    }>(
      route(ENDPOINTS.RECENT, {
        organization_id: organizationId,
        user_id: userId,
      }),
      { ...config, params: { ...config?.params, ...params } },
    );
    return {
      file_records: response.data.data.file_records,
      pagination: response.data.pagination ?? { has_more: false },
    };
  }

  /**
   * List workspace-class file records carrying a resource tag (e.g.
   * "favorite") across every workspace the user can view. The server
   * resolves accessible workspaces; no class/scope params are needed.
   */
  async listByTag(
    tag: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: FileRecordListResponse;
    }>(
      route(
        ENDPOINTS.LIST,
        { organization_id: organizationId, user_id: userId },
        { tag },
      ),
      config,
    );
    return response.data.data.file_records;
  }

  async getById(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: FileRecordResponse;
    }>(
      route(ENDPOINTS.GET, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      config,
    );
    return response.data.data.file_record;
  }

  async create(
    payload: CreateFileRecordPayload,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: FileRecordResponse;
    }>(
      route(ENDPOINTS.CREATE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      payload,
      config,
    );
    return response.data.data.file_record;
  }

  async delete(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      config,
    );
  }

  async presignUpload(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<PresignUploadResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: PresignUploadResponse;
    }>(
      route(ENDPOINTS.PRESIGN_UPLOAD, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      config,
    );
    return response.data.data;
  }

  async copy(
    fileRecordId: string,
    payload: CopyFileRecordPayload,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: FileRecordResponse;
    }>(
      route(ENDPOINTS.COPY, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      payload,
      config,
    );
    return response.data.data.file_record;
  }

  async move(
    fileRecordId: string,
    payload: MoveFileRecordPayload,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: FileRecordResponse;
    }>(
      route(ENDPOINTS.MOVE, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      payload,
      config,
    );
    return response.data.data.file_record;
  }

  /**
   * Mint a short-lived Tiptap Document Server JWT scoped to this file record (write, or
   * read+comment when the user only has view access).
   */
  async tiptapToken(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<TiptapTokenResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: TiptapTokenResponse;
    }>(
      route(ENDPOINTS.TIPTAP_TOKEN, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      {},
      config,
    );
    return response.data.data;
  }

  /**
   * Persist `tiptap_enabled` for this file record after TipTap Cloud has a document
   * (server checks HEAD). Idempotent when already enabled.
   */
  async tiptapEnable(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: TiptapEnableResponse;
    }>(
      route(ENDPOINTS.TIPTAP_ENABLE, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      {},
      config,
    );
    return response.data.data.file_record;
  }

  /** TipTap Conversion API JWT for PDF/DOCX export via Conversion REST API. */
  async tiptapConversionToken(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<TiptapConversionTokenResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: TiptapConversionTokenResponse;
    }>(
      route(ENDPOINTS.TIPTAP_CONVERSION_TOKEN, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      {},
      config,
    );
    return response.data.data;
  }

  /**
   * Patch the inner content of one or more named regions (identified by
   * `data-edit-id`) within an HTML file record. All regions are applied in a
   * single read-modify-write so concurrent autosaves stay consistent.
   */
  async patchRegion(
    fileRecordId: string,
    payload: PatchFileRecordRegionPayload,
    config?: AxiosRequestConfig,
  ): Promise<PatchFileRecordRegionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.patch<{
      data: PatchFileRecordRegionResponse;
    }>(
      route(ENDPOINTS.PATCH_REGION, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      payload,
      config,
    );
    return response.data.data;
  }

  /**
   * List historical S3 object versions for a file record. The server
   * filters to `processed` versions within the last 30 days and excludes
   * the currently-canonical `provider_version_id`. `summary` is stripped
   * from the list response to keep payloads compact — fetch the canonical
   * record via {@link getById} if you need it.
   */
  async listVersions(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecordVersion[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: ListFileRecordVersionsResponse;
    }>(
      route(ENDPOINTS.LIST_VERSIONS, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      config,
    );
    return response.data.data.file_record_versions;
  }

  /**
   * Roll the file record's content back to a specific historical version.
   * Server-side guards: only S3 records, no folders, version must be
   * `processed`, must have a `provider_version_id`, and must be within
   * the 30-day restore window. The returned `FileRecord` reflects the
   * post-restore state (typically back to `processing` while ingest
   * re-runs against the restored bytes).
   */
  /**
   * Publish a file record: copy its current content to the `public/{file_id}`
   * S3 prefix, set `published = true`, and stamp `published_content_hash`
   * with the just-published hash. The returned record carries the new
   * publish state, `view_url` (the viewer page to give a person) and
   * `raw_url` (the bytes).
   */
  async publish(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: FileRecordResponse;
    }>(
      route(ENDPOINTS.PUBLISH, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      undefined,
      config,
    );
    return response.data.data.file_record;
  }

  /**
   * Unpublish a previously-published file record. Removes the public copy
   * and clears `published` / `published_content_hash` on the record.
   */
  async unpublish(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: FileRecordResponse;
    }>(
      route(ENDPOINTS.UNPUBLISH, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      undefined,
      config,
    );
    return response.data.data.file_record;
  }

  /**
   * Bulk-presign URLs for up to {@link BULK_PRESIGN_FILE_RECORD_URLS_LIMIT}
   * file records in a single request. The server returns one item per
   * requested id (preserving order); per-item failures surface as
   * `item.error` rather than failing the whole call. Use this in place of
   * fanning out per-id `getById` calls when you only need the presigned URL
   * (e.g. rewriting `https://file.{env}.cohesive.ai/public/{id}` references
   * inside an HTML preview to short-lived inline-friendly links).
   *
   * Throws synchronously on an empty `fileRecordIds` list (the server would
   * 400 anyway) and on the size cap so callers don't have to special-case it.
   */
  async bulkPresignURLs(
    fileRecordIds: string[],
    config?: AxiosRequestConfig,
  ): Promise<BulkPresignFileRecordURLsItem[]> {
    if (fileRecordIds.length === 0) return [];
    if (fileRecordIds.length > BULK_PRESIGN_FILE_RECORD_URLS_LIMIT) {
      throw new Error(
        `bulkPresignURLs: too many file_record_ids (got ${fileRecordIds.length}, max ${BULK_PRESIGN_FILE_RECORD_URLS_LIMIT})`,
      );
    }

    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: BulkPresignFileRecordURLsResponse;
    }>(
      route(ENDPOINTS.BULK_PRESIGN_URLS, {
        organization_id: organizationId,
        user_id: userId,
      }),
      { file_record_ids: fileRecordIds },
      config,
    );
    return response.data.data.items;
  }

  /**
   * Publish multiple file records concurrently using the single-record
   * `/publish` endpoint. There is no server-side batch publish today, so
   * this is a thin client-side fan-out that preserves per-id outcomes via
   * `Promise.allSettled` semantics — callers can surface partial failures
   * (e.g. "1 of 5 linked files failed to publish") without conflating them
   * with the overall request.
   *
   * Order of the returned array matches `fileRecordIds` exactly.
   */
  async publishMany(
    fileRecordIds: string[],
    config?: AxiosRequestConfig,
  ): Promise<PublishManyResult[]> {
    if (fileRecordIds.length === 0) return [];

    const settled = await Promise.allSettled(
      fileRecordIds.map((id) => this.publish(id, config)),
    );
    return settled.map((res, i) => {
      const file_record_id = fileRecordIds[i];
      return res.status === "fulfilled"
        ? { file_record_id, status: "fulfilled", file_record: res.value }
        : { file_record_id, status: "rejected", reason: res.reason };
    });
  }

  /**
   * Unpublish multiple file records concurrently using the single-record
   * `/unpublish` endpoint. Mirrors `publishMany` — client-side fan-out via
   * `Promise.allSettled` so callers can surface partial failures.
   *
   * Order of the returned array matches `fileRecordIds` exactly.
   */
  async unpublishMany(
    fileRecordIds: string[],
    config?: AxiosRequestConfig,
  ): Promise<UnpublishManyResult[]> {
    if (fileRecordIds.length === 0) return [];

    const settled = await Promise.allSettled(
      fileRecordIds.map((id) => this.unpublish(id, config)),
    );
    return settled.map((res, i) => {
      const file_record_id = fileRecordIds[i];
      return res.status === "fulfilled"
        ? { file_record_id, status: "fulfilled", file_record: res.value }
        : { file_record_id, status: "rejected", reason: res.reason };
    });
  }

  /**
   * Restore a recently-deleted file record. The server enforces the 14-day
   * window, S3-only, and that a `provider_version_id` is still present; on
   * success the response carries the record with `status` flipped back to its
   * active value (`processed` for most cases).
   */
  /**
   * Lists recently deleted S3 files (restorable for 14 days) across the
   * workspaces and canvases the user can view, newest first.
   */
  async listArchived(
    params?: { limit?: number; before_ts?: string },
    config?: AxiosRequestConfig,
  ): Promise<ArchivedFileRecordListResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.get<ArchivedFileRecordListResponse>(
        route(ENDPOINTS.ARCHIVED, {
          organization_id: organizationId,
          user_id: userId,
        }),
        { ...config, params: { ...config?.params, ...params } },
      );
    return response.data;
  }

  async recover(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: RecoverFileRecordResponse;
    }>(
      route(ENDPOINTS.RECOVER, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      undefined,
      config,
    );
    return response.data.data.file_record;
  }

  /**
   * Add a reference to a Library (workspace-class) file in a canvas or chat.
   * Exactly one of `canvas_id` / `flow_session_id` must be set in the payload.
   * One reference per file per container: re-adding returns the existing row
   * with `existing: true` instead of erroring. 422 when the target is not a
   * Library file — add it to the Library first.
   */
  async createReference(
    fileRecordId: string,
    payload: CreateFileRecordReferencePayload,
    config?: AxiosRequestConfig,
  ): Promise<CreateFileRecordReferenceResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: CreateFileRecordReferenceResponse;
    }>(
      route(ENDPOINTS.REFERENCES, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      payload,
      config,
    );
    return response.data.data;
  }

  /**
   * The live references pointing at this Library file, limited to containers
   * the user can view — use for the archive/move warning dialogs
   * ("Referenced in N places").
   */
  async getReferences(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecordReferencesResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: FileRecordReferencesResponse;
    }>(
      route(ENDPOINTS.REFERENCES_LIST, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      config,
    );
    return response.data.data;
  }

  /**
   * Move a canvas/chat file into its workspace's files (same id — shares,
   * chat attachments, and canvas nodes keep working) and leave a reference
   * behind in its original container. `workspace_id` is only needed when the
   * source container has no workspace (standalone chats).
   */
  /**
   * Download a record's bytes through the API. Canvas blocks come back baked
   * for egress (token stylesheet + display stamps); other records redirect to
   * their presigned URL, which axios follows transparently.
   */
  async downloadOriginal(
    fileRecordId: string,
    config?: AxiosRequestConfig,
  ): Promise<Blob> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<Blob>(
      route(ENDPOINTS.DOWNLOAD, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      { responseType: "blob", ...config },
    );
    return response.data;
  }

  async addToWorkspace(
    fileRecordId: string,
    payload?: AddFileRecordToWorkspacePayload,
    config?: AxiosRequestConfig,
  ): Promise<AddFileRecordToWorkspaceResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: AddFileRecordToWorkspaceResponse;
    }>(
      route(ENDPOINTS.ADD_TO_LIBRARY, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
      }),
      payload ?? {},
      config,
    );
    return response.data.data;
  }

  async restoreVersion(
    fileRecordId: string,
    fileRecordVersionId: string,
    config?: AxiosRequestConfig,
  ): Promise<FileRecord> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: RestoreFileRecordVersionResponse;
    }>(
      route(ENDPOINTS.RESTORE_VERSION, {
        organization_id: organizationId,
        user_id: userId,
        file_record_id: fileRecordId,
        file_record_version_id: fileRecordVersionId,
      }),
      undefined,
      config,
    );
    return response.data.data.file_record;
  }
}
